import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const LANGS = [
  { code: 'es', flag: '\uD83C\uDDEA\uD83C\uDDF8', name: 'Espanol' },
  { code: 'en', flag: '\uD83C\uDDEC\uD83C\uDDE7', name: 'English' },
  { code: 'fr', flag: '\uD83C\uDDEB\uD83C\uDDF7', name: 'Francais' },
  { code: 'de', flag: '\uD83C\uDDE9\uD83C\uDDEA', name: 'Deutsch' },
  { code: 'it', flag: '\uD83C\uDDEE\uD83C\uDDF9', name: 'Italiano' },
  { code: 'pt', flag: '\uD83C\uDDF5\uD83C\uDDF9', name: 'Portugues' },
  { code: 'ja', flag: '\uD83C\uDDEF\uD83C\uDDF5', name: 'Japanese' },
  { code: 'ko', flag: '\uD83C\uDDF0\uD83C\uDDF7', name: 'Korean' },
  { code: 'zh', flag: '\uD83C\uDDE8\uD83C\uDDF3', name: 'Chinese' },
  { code: 'ru', flag: '\uD83C\uDDF7\uD83C\uDDFA', name: 'Russian' },
  { code: 'ar', flag: '\uD83C\uDDF8\uD83C\uDDE6', name: 'Arabic' },
  { code: 'nl', flag: '\uD83C\uDDF3\uD83C\uDDF1', name: 'Nederlands' },
];

function AlcazarKnowledge() {
  const { user } = useAuth();
  const isEditor = user && ['superadmin', 'admin', 'revisor'].includes(user.role);

  const [allTopics, setAllTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [lang, setLang] = useState('es');
  const pageSize = 30;

  // Editing state
  const [editingTopic, setEditingTopic] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editLang, setEditLang] = useState('es');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // History modal
  const [historyTopic, setHistoryTopic] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [translating, setTranslating] = useState(null);

  const fetchTopics = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/alcazar/knowledge', { params: { lang } });
      if (res.data.success) {
        setAllTopics(res.data.topics || []);
      }
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) return;
      setError('Error al cargar conocimiento');
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { fetchTopics(); }, [fetchTopics]);

  // Client-side filter + paginate
  const filtered = useMemo(() => {
    if (!search) return allTopics;
    const q = search.toLowerCase();
    return allTopics.filter(t =>
      String(t.display_id || t.id).includes(q) || String(t.id).includes(q) ||
      t.topic.toLowerCase().includes(q) ||
      (t.content || '').toLowerCase().includes(q)
    );
  }, [allTopics, search]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const topics = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [search, lang]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const startEdit = (topic) => {
    setEditingTopic(topic.topic);
    setEditContent(topic.content || '');
    setEditLang(lang);
  };

  const cancelEdit = () => {
    setEditingTopic(null);
    setEditContent('');
  };

  const saveEdit = async () => {
    if (!editContent.trim()) return;
    setSaving(true);
    try {
      const res = await api.put(`/api/alcazar/knowledge/${encodeURIComponent(editingTopic)}`, {
        content: editContent.trim(),
        lang: editLang,
      });
      if (res.data.success) {
        showToast(`Guardado: ${editingTopic} (${editLang})`);
        setEditingTopic(null);
        setEditContent('');
        fetchTopics();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (topicName) => {
    setHistoryTopic(topicName);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/api/alcazar/knowledge/${encodeURIComponent(topicName)}/history`, {
        params: { lang }
      });
      setHistoryData(res.data.history || []);
    } catch {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };


  const translateAll = async (topicName) => {
    setTranslating(topicName);
    try {
      const res = await api.post(`/api/alcazar/knowledge/${encodeURIComponent(topicName)}/translate-all`, {
        source_lang: lang,
      });
      if (res.data.success) {
        const ok = res.data.results.filter(r => r.status === 'ok').length;
        showToast(`Traducido ${topicName} a ${ok} idiomas`);
        fetchTopics();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al traducir', 'error');
    } finally {
      setTranslating(null);
    }
  };

  const deleteTopic = async (topicName) => {
    if (!window.confirm('Eliminar "' + topicName + '" en todos los idiomas?')) return;
    try {
      const res = await api.delete('/api/alcazar/knowledge/' + encodeURIComponent(topicName));
      if (res.data.success) {
        showToast('Eliminado: ' + topicName + ' (' + res.data.deleted + ' entries)');
        fetchTopics();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al eliminar', 'error');
    }
  };

  const langInfo = LANGS.find(l => l.code === lang) || LANGS[0];

  return (
    <div className="space-y-4">
      {/* Warning banner */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
        <strong>Patronato solo:</strong> reclamacion_formal + reservas_escolares. Todo lo demas &#8594; HANDOFF_ENTRADAS.
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-white font-medium ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Buscar por ID, topic o contenido..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1"
            />
          </div>
          <select
            value={lang}
            onChange={e => setLang(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            {LANGS.map(l => (
              <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
            ))}
          </select>
          <button onClick={() => window.open("/api/alcazar/knowledge/export-print?lang=" + lang + "&token=" + localStorage.getItem("token"), "_blank")} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition">Exportar</button>
          <span className="text-sm text-gray-500">
            {filtered.length} de {allTopics.length} topics
          </span>
        </div>
      </div>

      {/* Topics table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-3 text-gray-500">Cargando...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-12">ID</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{minWidth:120}}>Topic</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pregunta</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24">Opciones</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-28">Revisado</th>
                </tr>
              </thead>
              <tbody>
                {topics.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No se encontraron topics</td></tr>
                ) : topics.map((t, idx) => (
                  <React.Fragment key={t.topic}>
                    {/* Row 1: Question */}
                    <tr className="hover:bg-gray-50 border-t border-gray-100">
                      <td className="px-3 py-1.5 text-xs text-gray-400 align-middle" rowSpan={editingTopic === t.topic ? 1 : 2}>{t.display_id || t.id}</td>
                      <td className="px-3 py-1.5 align-middle" rowSpan={editingTopic === t.topic ? 1 : 2}>
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">{t.topic}</span>
                      </td>
                      <td className="px-3 py-1.5 text-sm font-semibold text-gray-800">
                        {t.faq_question || <span className="text-gray-400 italic">Sin pregunta</span>}
                      </td>
                      <td className="px-3 py-1.5 text-center align-middle" rowSpan={editingTopic === t.topic ? 1 : 2}>
                        <div className="flex items-center justify-center gap-1">
                          {isEditor && editingTopic !== t.topic && (
                            <button onClick={() => startEdit(t)} title="Editar"
                              className="p-1 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                            </button>
                          )}
                          <button onClick={() => openHistory(t.topic)} title="Historial"
                            className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>
                          </button>
                          {isEditor && (
                            <button onClick={() => translateAll(t.topic)} title="Traducir"
                              disabled={translating === t.topic}
                              className="p-1 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50">
                              {translating === t.topic ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 animate-spin" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/></svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389 21.034 21.034 0 01-.554-.6 18.902 18.902 0 01-3.36 3.04 1 1 0 11-1.11-1.664 16.89 16.89 0 003.073-2.852 17.007 17.007 0 01-1.303-2.635 1 1 0 011.856-.742 14.99 14.99 0 00.96 1.887A16.878 16.878 0 007.578 6H3a1 1 0 110-2h3V3a1 1 0 011-1zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.724 1.447a1 1 0 11-1.789-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z" clipRule="evenodd"/></svg>
                              )}</button>
                          )}
                          {isEditor && (
                            <button onClick={() => deleteTopic(t.topic)} title="Eliminar"
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-center align-middle" rowSpan={editingTopic === t.topic ? 1 : 2}>
                        <div className="flex items-center justify-center gap-1">
                          {t.verified ? (
                            <>
                              <span className="text-green-500">&#10003;</span>
                              <span className="text-xs text-gray-500 truncate max-w-[70px]">{t.taught_by ? t.taught_by.split('@')[0] : ''}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-gray-300">&#10003;</span>
                              <span className="text-xs text-gray-400">Sin revisar</span>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Row 2: Answer (or edit form) */}
                    {editingTopic === t.topic ? (
                      <tr>
                        <td colSpan={5} className="px-3 pb-3">
                          <div className="space-y-2">
                            <select value={editLang} onChange={e => { setEditLang(e.target.value); setEditContent(t.content || ''); }}
                              className="border border-gray-300 rounded px-2 py-1 text-xs bg-white">
                              {LANGS.map(l => (<option key={l.code} value={l.code}>{l.flag} {l.name}</option>))}
                            </select>
                            <textarea value={editContent} onChange={e => setEditContent(e.target.value)}
                              className="w-full border border-green-300 rounded-lg p-2 text-sm min-h-[100px] focus:ring-2 focus:ring-green-500" />
                            <div className="flex gap-2">
                              <button onClick={saveEdit} disabled={saving}
                                className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50">
                                {saving ? 'Guardando...' : 'Guardar'}</button>
                              <button onClick={cancelEdit}
                                className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300">Cancelar</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 pb-2 text-xs text-gray-500 leading-relaxed" colSpan={1}>
                          {(t.content || '').length > 150
                            ? t.content.substring(0, 150) + '...'
                            : t.content || <span className="text-gray-400 italic">Sin contenido en {lang.toUpperCase()}</span>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Pagina {page} de {totalPages} ({filtered.length} topics)
              </span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50">
                  Anterior
                </button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`px-3 py-1 text-sm border rounded ${
                        p === page ? 'bg-green-600 text-white' : 'bg-white hover:bg-gray-50'
                      }`}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50">
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Modal */}
      {historyTopic && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="bg-blue-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Historial de cambios</h3>
                <p className="text-sm text-blue-200">{historyTopic}</p>
              </div>
              <button onClick={() => setHistoryTopic(null)}
                className="text-white/80 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <div className="overflow-auto flex-1 p-4">
              {historyLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : historyData.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Sin historial de cambios</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">Fecha</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">Usuario</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">Idioma</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">Accion</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">Contenido</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historyData.map(h => (
                      <tr key={h.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {new Date(h.created_at).toLocaleString('es-ES', {
                            day: '2-digit', month: '2-digit', year: '2-digit',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="px-3 py-2 text-gray-800">{h.changed_by_name || h.changed_by_email || '-'}</td>
                        <td className="px-3 py-2">
                          <span className="uppercase font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">{h.language}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            h.action === 'CREATE' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                          }`}>{h.action}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={h.content_after}>
                          {(h.content_after || '').substring(0, 100)}...
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlcazarKnowledge;
